// src/components/charts/CaMensuelChart.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import zoomPlugin from 'chartjs-plugin-zoom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend, zoomPlugin );

type Mode = 'factures' | 'reglements';

type Serie = { name: string; data: number[] };
type ApiResponse = {
  date_ref: string;
  years: number[];
  months: number[];           // 1..12
  factures?: Serie[];         // [{name:'2022', data:[...12]}]
  reglements?: Serie[];
};

type Props = {
  /** date de référence, ex: '2025-08-10'. Si non fourni, le backend prendra “aujourd’hui”. */
  date?: string;
  /** mode initial (CA = factures / règlements) */
  initialMode?: Mode;
  /** classe utilitaire optionnelle (pour intégrer dans tes cards/masonry) */
  className?: string;
  /** hauteur du canvas */
  height?: number; // px
};

const FR_MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

// Palette de couleurs pro (tons Tailwind-ish)
const PALETTE = [
  '#4f46e5', // indigo-600
  '#06b6d4', // cyan-500
  '#22c55e', // green-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#a855f7', // purple-500
  '#14b8a6', // teal-500
  '#3b82f6', // blue-500
  '#e11d48', // rose-600
  '#84cc16', // lime-500
];

function fmtMoney(n: number) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
}

export default function CaMensuelChart({
  date,
  initialMode = 'factures',
  className = '',
  height = 320,
}: Props) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [payload, setPayload] = useState<ApiResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');
        const res = await api.get<ApiResponse>('/ca-mensuel-chart', {
          params: { ...(date ? { date } : {}), mode: 'both' }, // on récupère tout, on switch côté UI
        });
        if (!cancelled) setPayload(res.data);
      } catch (e: any) {
        if (!cancelled) setErr(e?.response?.data?.error || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date]);

  const chartData: ChartData<'line'> | null = useMemo(() => {
    if (!payload) return null;

    const labels = FR_MONTHS; // 12 labels
    const series: Serie[] = mode === 'factures' ? (payload.factures || []) : (payload.reglements || []);

    const datasets = series.map((s, i) => {
      const color = PALETTE[i % PALETTE.length];
      return {
        label: s.name,
        data: s.data,
        borderColor: color,
        backgroundColor: color + '33', // légère transparence
        fill: false,
        tension: 0.25,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
      };
    });

    return { labels, datasets };
  }, [payload, mode]);

  const options: ChartOptions<'line'> = useMemo(
    () => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
        legend: { position: 'top', labels: { usePointStyle: true } },
        tooltip: {
            callbacks: {
            label: (ctx) => {
                const label = ctx.dataset.label || '';
                const val = typeof ctx.parsed.y === 'number' ? fmtMoney(ctx.parsed.y) : '-';
                return ` ${label}: ${val}`;
            },
            },
        },
        zoom: {
            zoom: {
            wheel: {
                enabled: true, // zoom à la molette
            },
            pinch: {
                enabled: true, // zoom tactile
            },
            mode: 'x', // zoom sur l’axe X uniquement
            },
            pan: {
            enabled: true,
            mode: 'x',
            },
        },
        },
        scales: {
        y: {
            ticks: {
            callback: (v) => fmtMoney(Number(v)).replace(/\s€/,' €'),
            },
            grid: { color: 'rgba(0,0,0,0.06)' },
        },
        x: {
            grid: { display: false },
        },
        },
    }),
    []
    );

  return (
    <div className={`rounded-2xl border border-gray-200 bg-white px-5 pb-4 pt-5 dark:border-darkBorderCard dark:bg-white/[0.03] sm:px-6 sm:pt-6 ${className}`}>
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            {mode === 'factures' ? 'Chiffre d’affaires cumulatif' : 'Règlements cumulatif'}
          </h3>
          {/* {payload?.date_ref && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Période de référence : {payload.date_ref} • Années: {payload?.years?.join(', ')}
            </p>
          )} */}
        </div>

        <div className="inline-flex overflow-hidden rounded-lg bg-gray-100 p-0.5 dark:bg-gray-900">
          <button
            onClick={() => setMode('factures')}
            className={[
              'px-3 py-2 text-sm font-medium transition',
              mode === 'factures'
                ? 'bg-white shadow-sm text-gray-900 dark:bg-gray-800 dark:text-white'
                : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200',
            ].join(' ')}
            aria-pressed={mode === 'factures'}
          >
            CA
          </button>
          <button
            onClick={() => setMode('reglements')}
            className={[
              'px-3 py-2 text-sm font-medium transition',
              mode === 'reglements'
                ? 'bg-white shadow-sm text-gray-900 dark:bg-gray-800 dark:text-white'
                : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200',
            ].join(' ')}
            aria-pressed={mode === 'reglements'}
          >
            Règlements
          </button>
        </div>
      </div>

      {/* Body */}
      {loading && <div className="text-gray-500">Chargement…</div>}
      {err && !loading && <div className="text-red-600">{err}</div>}
      {!loading && !err && chartData && (
        <div style={{ height }}>
          <Line data={chartData} options={options} />
        </div>
      )}
    </div>
  );

  
}
